import { z } from 'src/lib';
import { EntidadCacheFields, OperacionADCMFields } from './fields';
import { FilePutFields } from 'src/pages/sistema/api/file-fields';
import { schemaEntityWithIdAndDescription } from 'src/types';
import { ADCMClienteTipo, ADCMTransaccionTipo } from './enum';
import { schemaStringObligatorio, schemaStringOpcional } from 'src/pages/sistema/types/types';

const booleanTransform = (valor: any) => {
  if (typeof valor === 'boolean') return valor;
  if (valor == 1) return true;
  if (valor == 2) return false;
  return null;
};

const schemaBooleanADCM = z.preprocess(booleanTransform, z.boolean({
  invalid_type_error: "Requerido"
}));

const schemaBooleanADCMNullable = z.preprocess(booleanTransform, z.boolean().nullable().optional());

export const schemaOperacionADCMInsert = z.object({
  [OperacionADCMFields.CodPais]: z.number(),
  [OperacionADCMFields.CodUnidadNegocio]: z.number(),
  [OperacionADCMFields.CodSectorComercial]: z.number(),
  [OperacionADCMFields.FechaSolicitud]: z.date(),

  // [OperacionADCMFields.RazonSocialEmisor]: z.string().min(1, 'La razón social es requerida'),
  [OperacionADCMFields.RazonSocialEmisor]: schemaStringObligatorio,
  [OperacionADCMFields.CodEmisorTipo]: z.number(),
  [OperacionADCMFields.CodClienteTipo]: z.number(),
  [OperacionADCMFields.NroCuenta]: schemaStringOpcional,
  [OperacionADCMFields.EmisorPreaprobado]: schemaBooleanADCM,
  [OperacionADCMFields.RatingCrediticioEmisor]: schemaStringObligatorio,
  [OperacionADCMFields.Legislacion]: schemaStringObligatorio,
  [OperacionADCMFields.InformacionAdicionalEmisor]: z.string().nullable().optional(),

  [OperacionADCMFields.CodTransaccionTipo]: z.number(),
  [OperacionADCMFields.Underwriting]: schemaBooleanADCM,
  [OperacionADCMFields.Bridgeloan]: schemaBooleanADCM,
  [OperacionADCMFields.Monto]: schemaStringOpcional,
  [OperacionADCMFields.Moneda]: schemaStringOpcional,
  [OperacionADCMFields.DestinoFondos]: schemaStringOpcional,
  [OperacionADCMFields.CuponTransaccion]: schemaStringOpcional,
  [OperacionADCMFields.CadenciaPagoCupon]: schemaStringOpcional,
  [OperacionADCMFields.PlazoVencimiento]: schemaStringOpcional,
  [OperacionADCMFields.CadenciaAmortizacion]: schemaStringOpcional,
  [OperacionADCMFields.Fee]: schemaStringOpcional,
  [OperacionADCMFields.ContratoExclusividad]: schemaBooleanADCMNullable,
  [OperacionADCMFields.Colocadores]: schemaStringOpcional,
  [OperacionADCMFields.Garantias]: schemaStringOpcional,

  [OperacionADCMFields.UnderWritingMonto]: schemaStringOpcional,
  [OperacionADCMFields.UnderWritingFee]: schemaStringOpcional,
  [OperacionADCMFields.UnderWritingObservaciones]: schemaStringOpcional,

  [OperacionADCMFields.BridgeLoanMoneda]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanMonto]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanTasaInteres]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanPlazo]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanFrecuenciaPagoInteres]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanGarantias]: schemaBooleanADCMNullable,
  [OperacionADCMFields.BridgeLoanDetalleGarantias]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanPrecancelable]: schemaBooleanADCMNullable,
  [OperacionADCMFields.BridgeLoanCondicionesPrecancelacion]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanDetalleAforo]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanTipoAmortizacionCapital]: schemaStringOpcional,
  [OperacionADCMFields.BridgeLoanComisionEstructuracion]: schemaStringOpcional,


  [OperacionADCMFields.Observaciones]: z.string().nullable().optional(),

  [FilePutFields.File]: z.instanceof(File).optional().nullable(),

  [OperacionADCMFields.CodVirtualCliente]: z.number().optional().nullable(),
  [OperacionADCMFields.CodVirtualTransaccion]: z.number().optional().nullable(),
  [OperacionADCMFields.CodVirtualEmisor]: z.number().optional().nullable(),

}).refine(data =>
  !(data[OperacionADCMFields.CodVirtualCliente] === ADCMClienteTipo.Cliente && !data[OperacionADCMFields.NroCuenta]?.trim()),
  {
    message: "El número de cuenta es obligatorio",
    path: [OperacionADCMFields.NroCuenta],
  }).superRefine((data, ctx) => {
    // tipo virtual de la transaccion
    const tipoT = data[OperacionADCMFields.CodVirtualTransaccion];

    // Generales
    if (tipoT === ADCMTransaccionTipo.DCM || tipoT === ADCMTransaccionTipo.ECM || tipoT === ADCMTransaccionTipo.ABS) {
      if (!data[OperacionADCMFields.Monto]?.trim()) Validar(ctx, OperacionADCMFields.Monto);
      if (!data[OperacionADCMFields.Moneda]?.trim()) Validar(ctx, OperacionADCMFields.Moneda);
      if (!data[OperacionADCMFields.DestinoFondos]?.trim()) Validar(ctx, OperacionADCMFields.DestinoFondos);
      if (!data[OperacionADCMFields.Fee]?.trim()) Validar(ctx, OperacionADCMFields.Fee);
    }

    // Transaccion DCM  y ABS
    if (tipoT === ADCMTransaccionTipo.DCM || tipoT === ADCMTransaccionTipo.ABS) {
      if (!data[OperacionADCMFields.CuponTransaccion]?.trim()) Validar(ctx, OperacionADCMFields.CuponTransaccion);
      if (!data[OperacionADCMFields.CadenciaPagoCupon]?.trim()) Validar(ctx, OperacionADCMFields.CadenciaPagoCupon);
      if (!data[OperacionADCMFields.PlazoVencimiento]?.trim()) Validar(ctx, OperacionADCMFields.PlazoVencimiento);
      if (!data[OperacionADCMFields.CadenciaAmortizacion]?.trim()) Validar(ctx, OperacionADCMFields.CadenciaAmortizacion);
      if (!data[OperacionADCMFields.Colocadores]?.trim()) Validar(ctx, OperacionADCMFields.Colocadores);

      // Validación para el booleano (Contrato Exclusividad)
      if (data[OperacionADCMFields.ContratoExclusividad] === null) {
        Validar(ctx, OperacionADCMFields.ContratoExclusividad);
      }
    }

    // Transaccion ABS
    if (tipoT === ADCMTransaccionTipo.ABS) {
      if (!data[OperacionADCMFields.Garantias]?.trim()) Validar(ctx, OperacionADCMFields.Garantias);
    }



    // Validaciones de underwriting
    if (data[OperacionADCMFields.Underwriting] === true) {

      const monto = data[OperacionADCMFields.UnderWritingMonto];
      if (!monto) {
        Validar(ctx, OperacionADCMFields.UnderWritingMonto);
      }

      const fee = data[OperacionADCMFields.UnderWritingFee];
      if (!fee) {
        Validar(ctx, OperacionADCMFields.UnderWritingFee);
      }

      if (!data[OperacionADCMFields.UnderWritingObservaciones]?.trim()) {
        Validar(ctx, OperacionADCMFields.UnderWritingObservaciones);
      }
    }


    // Validaciones bridge loan
    if (data[OperacionADCMFields.Bridgeloan] === true) {
      if (!data[OperacionADCMFields.BridgeLoanMoneda]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanMoneda);
      if (!data[OperacionADCMFields.BridgeLoanMonto]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanMonto);
      if (!data[OperacionADCMFields.BridgeLoanComisionEstructuracion]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanComisionEstructuracion);
      if (!data[OperacionADCMFields.BridgeLoanTasaInteres]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanTasaInteres);
      if (!data[OperacionADCMFields.BridgeLoanPlazo]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanPlazo);
      if (!data[OperacionADCMFields.BridgeLoanTipoAmortizacionCapital]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanTipoAmortizacionCapital);
      if (!data[OperacionADCMFields.BridgeLoanFrecuenciaPagoInteres]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanFrecuenciaPagoInteres);

      // Garantias
      if (data[OperacionADCMFields.BridgeLoanGarantias] === null) Validar(ctx, OperacionADCMFields.BridgeLoanGarantias);

      // si Garantías es "SI" (true)
      if (data[OperacionADCMFields.BridgeLoanGarantias] === true) {
        if (!data[OperacionADCMFields.BridgeLoanDetalleGarantias]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanDetalleGarantias);
        if (!data[OperacionADCMFields.BridgeLoanDetalleAforo]?.trim()) Validar(ctx, OperacionADCMFields.BridgeLoanDetalleAforo);
      }

      // Precancelable
      if (data[OperacionADCMFields.BridgeLoanPrecancelable] === null) Validar(ctx, OperacionADCMFields.BridgeLoanPrecancelable);

      // si Precancelable es "SI" (true)
      if (data[OperacionADCMFields.BridgeLoanPrecancelable] === true) {
        if (data[OperacionADCMFields.BridgeLoanCondicionesPrecancelacion] === null) {
          Validar(ctx, OperacionADCMFields.BridgeLoanCondicionesPrecancelacion);
        }
      }
    }
  });

const Validar = (ctx: any, campo: string, message?: string) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: message || "Campo requerido",
    path: [campo],
  });
};

export type OperacionADCMInsert = z.infer<typeof schemaOperacionADCMInsert>;

